<template>
  <section class="hero dark" id="hero">
    <div class="container">
      <div class="row">
        <div class="col-lg-8">
          <p class="hero-kicker">{{ t('Everything you need in just one subscription!') }}</p>
          <h1 class="hero-title">
            {{ t('Develop your') }}
            <span class="hero-line">
              <span class="teletype" data-event="teletype"></span>
              {{ t('Website') }}
            </span>
            {{ t('with Veximus.') }}
          </h1>
          <p>
            <mark>
              <font-awesome-icon class="me-1" icon="stopwatch" />
              {{ t('Limited Time Offer: Free Web Hosting + Free Top-Level Domain +') }} <br />{{ t('Free SSL + Free CDN + Free Business Emails.') }}
            </mark>
          </p>
          <div class="timer" data-event="timer">
            <div data-type="days">
              <span>00</span>
              <label>{{ t('Days') }}</label>
            </div>
            <div data-type="hours">
              <span>00</span>
              <label>{{ t('Hours') }}</label>
            </div>
            <div data-type="minutes">
              <span>00</span>
              <label>{{ t('Minutes') }}</label>
            </div>
            <div data-type="seconds">
              <span>00</span>
              <label>{{ t('Seconds') }}</label>
            </div>
          </div>
        </div>
        <div class="col-12">
          <div class="devices">
            <div class="device phone">
              <img class="frame" src="/images/home/hero/phone-frame.png" alt="Phone Frame" />
              <img class="content" src="/images/home/hero/phone-content.jpg" alt="Phone Content" />
              <img class="shadow" src="/images/home/hero/phone-shadow.png" alt="Phone Shadow" />
            </div>
            <div class="device laptop">
              <img class="frame" src="/images/home/hero/laptop-frame.png" alt="Laptop Frame" />
              <img class="content" src="/images/home/hero/laptop-content.jpg" alt="Laptop Content" />
              <img class="shadow" src="/images/home/hero/laptop-shadow.png" alt="Laptop Shadow" />
            </div>
            <div class="device tablet">
              <img class="frame" src="/images/home/hero/tablet-frame.png" alt="Tablet Frame" />
              <img class="content" src="/images/home/hero/tablet-content.jpg" alt="Tablet Content" />
              <img class="shadow" src="/images/home/hero/tablet-shadow.png" alt="Tablet Shadow" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

.hero {
  background: linear-gradient(90deg, $primary, $accent-blue);
  color: $on-dark-strong;
  padding-bottom: 0;
  padding-top: 10rem;
  position: relative;

  &::before {
    background: url('/images/home/hero/bg.png') no-repeat top;
    background-size: cover;
    bottom: 0;
    content: '';
    left: 0;
    opacity: 0.5;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
  }

  /* The home hero is the one bespoke hero on the site (teletype, mark,
     countdown), so unlike every other page it is not a PageHero. Its kicker is
     not a heading either: it is the same label role as SectionTitle's kicker,
     demoted from the h5 it used to be, because a label in the document outline
     is what made the home page report five h1 and a skip from h1 to h5.

     It now carries that shared spec as well, which it did not before: the
     outline had moved but the type had not, so the label role it claimed was
     the largest of the five sizes this treatment had drifted into. It is the
     shared $label-* step, uppercase in Rubik, in the blue band's white. It was
     $dark-accent, which is the light blue this ground cannot show: the hero is
     the blue gradient, so the label and the band were nearly the same colour. */
  .hero-kicker {
    color: $label-on-brand;
    font-family: $headings-font-family;
    font-size: $label-size;
    font-weight: $label-weight;
    letter-spacing: $label-tracking;
    margin-bottom: 0.9rem;
    text-transform: uppercase;
  }

  /* The hero title's own spec, named rather than declared on the bare h1 so the
     spec has a single owner and a page cannot restyle the hero by element. It is
     deliberately not PageHero's clamp(): the home hero is a full-bleed title over
     the device row and keeps its fixed two-step scale. */
  .hero-title {
    color: $on-dark-strong;
    font-size: 3.5rem;
    line-height: 3.75rem;
    margin-bottom: 1.5rem;
    position: relative;
    z-index: 5;

    /* A block line inside the title, where a div used to sit. h1 may only carry
       phrasing content, so the line break is a span with display: block. */
    .hero-line {
      display: block;
    }

    .teletype {
      color: $secondary;

      &::after {
        animation: flicker $dur-slow infinite;
        content: '|';
        font-weight: 300;
        margin-left: 0.25rem;
      }

      @media (max-width: 991.98px) {
        display: block;
      }
    }

    @media (max-width: 575.98px) {
      font-size: 2.5rem;
      line-height: 2.5rem;
    }
  }

  p {
    margin-bottom: 1.5rem;
    position: relative;
    z-index: 5;

    mark {
      background: $primary;
      color: $on-dark-strong;
      padding: 0.25rem 0;

      @media (max-width: 991.98px) {
        background: none;
      }

      br {
        @media (max-width: 575.98px) {
          display: none;
        }
      }
    }
  }

  .timer {
    margin-bottom: 2rem;
    position: relative;
    z-index: 5;

    > div {
      align-items: center;
      display: inline-flex;
      flex-direction: column;
      margin-right: 1rem;

      > span {
        align-items: center;
        background: $ground;
        border-radius: $r-lg;
        box-shadow: $shadow-dark;
        color: $primary;
        display: flex;
        font-family: $headings-font-family;
        font-size: 2.5rem;
        font-weight: 300;
        height: 70px;
        justify-content: center;
        letter-spacing: -0.1rem;
        margin-bottom: 0.5rem;
        position: relative;
        width: 70px;

        @media (max-width: 575.98px) {
          border-radius: $r-lg;
          font-size: 1.5rem;
          height: 45px;
          width: 45px;
        }
      }

      > label {
        background: $ground-dark;
        border-radius: $r-xs;
        display: block;
        font-size: 0.85rem;
        font-weight: 500;
        padding: 0 0.25rem;
        z-index: 5;
      }

      &:not(:last-child) {
        > span {
          &::after {
            color: $on-dark-strong;
            content: ':';
            position: absolute;
            right: -0.75rem;
            z-index: 5;
          }
        }
      }
    }
  }

  .devices {
    align-items: center;
    display: flex;
    justify-content: center;
    margin-bottom: 5rem;
    margin-top: -15rem;
    transform: rotate(-30deg);

    .device {
      position: relative;

      .frame {
        position: relative;
        z-index: 2;
      }

      .content {
        position: absolute;
        z-index: 1;
      }

      .shadow {
        box-shadow: none !important;
        max-width: initial;
        position: absolute;
      }

      &.phone {
        max-width: 9.5vw;

        .content {
          border-radius: 10%;
          left: 3%;
          width: calc(100% - 5%);
        }

        .shadow {
          left: -8%;
          top: -3.5%;
          width: 160%;
        }
      }

      &.laptop {
        max-width: 43vw;

        .content {
          left: 10%;
          top: 5%;
          width: calc(100% - 20%);
        }

        .shadow {
          left: -8%;
          top: -10%;
          width: 109%;
        }
      }

      &.tablet {
        max-width: 26vw;

        .frame {
          position: relative;
          z-index: 2;
        }

        .content {
          border-radius: 10%;
          left: 0;
          top: 0;
        }

        .shadow {
          left: 0;
          top: 0;
          width: 113%;
        }
      }
    }

    @media (max-width: 991.98px) {
      margin-top: -5rem;
      margin-bottom: 10rem;
    }
  }

  @keyframes flicker {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
}
</style>

<script>
import { startCountdown, stopCountdown } from '@/countdown';
import { useI18n } from 'vue-i18n';

export default {
  data() {
    return {
      /* The marquee is strictly sequential, so only one of its timeouts is ever
         pending and a single handle is enough. It used to be an array that grew
         for as long as the page was open, because nothing removed a handle once
         it had fired. */
      teletypeHandle: null,
      countdownHandle: null,
      /* Set once on teardown so a timeout that is already in flight stops
         instead of re-entering and scheduling the next one. */
      stopped: false
    };
  },

  methods: {
    typeString(target, string, pointer, delay, callback) {
      if (this.stopped) return;
      target.innerHTML += string[pointer];
      if (pointer < string.length - 1) {
        this.teletypeHandle = setTimeout(() => {
          this.typeString(target, string, pointer + 1, delay, callback);
        }, delay);
        return;
      }

      callback();
    },

    deleteString(target, delay, callback) {
      if (this.stopped) return;
      var length = target.innerHTML.length;

      target.innerHTML = target.innerHTML.substr(0, length - 1);
      if (length > 1) {
        this.teletypeHandle = setTimeout(() => {
          this.deleteString(target, delay, callback);
        }, delay);
        return;
      }

      callback();
    },

    teletype(target, options) {
      var component = this,
        settings = _.extend(
          {
            delay: 100,
            pause: 3000,
            text: []
          },
          options
        );

      if (!target) {
        return;
      }

      (function loop(target, index) {
        component.typeString(target, settings.text[index], 0, settings.delay, () => {
          component.teletypeHandle = setTimeout(() => {
            component.deleteString(target, settings.delay, () => {
              loop(target, (index + 1) % settings.text.length);
            });
          }, settings.pause);
        });
      })(target, 0);
    },

    /* Monday at midnight, the same window the offer band counts down to. */
    startClock() {
      var timer = new Date(),
        date = timer.getDate(),
        day = timer.getDay(),
        offset = (1 + 7 - day) % 7;

      timer.setDate(offset ? date + offset : date + 7);
      timer.setHours(0, 0, 0);

      startCountdown(this, timer);
    }
  },

  watch: {
    '$i18n.global.locale.value'(currentLocale, previousLocale) {
      var node = document.querySelector('[data-event="teletype"]');

      if (!node) return;

      node.innerHTML = '';
      clearTimeout(this.teletypeHandle);
      this.teletypeHandle = null;

      this.teletype(node, {
        text: [this.t('E-commerce'), this.t('Blog'), this.t('Portfolio'), this.t('Educational'), this.t('Showcase')]
      });
    }
  },

  mounted() {
    // Teletype
    this.teletype(document.querySelector('[data-event="teletype"]'), {
      text: [this.t('E-commerce'), this.t('Blog'), this.t('Portfolio'), this.t('Educational'), this.t('Showcase')]
    });

    this.startClock();
  },

  /* Both loops run for as long as the page is open, and Inertia unmounts this
     component the moment the reader leaves the home page. Without this teardown
     the marquee keeps scheduling against a detached node and the countdown
     keeps writing into one nobody can see. */
  beforeUnmount() {
    this.stopped = true;
    clearTimeout(this.teletypeHandle);
    this.teletypeHandle = null;
    stopCountdown(this);
  },

  setup() {
    const { t } = useI18n({
      messages: {
        /* The marquee names kinds of website ("Develop your ___ Website"), so
           `Blog` here is the noun for the site, not the nav label. The shared
           key now reads 博客文章 for the header, footer and breadcrumbs, and
           this local override keeps the marquee at 博客. */
        'en-MY': {
          'Develop your': 'Develop your',
          'E-commerce': 'E-commerce',
          Blog: 'Blog',
          'Everything you need in just one subscription!': 'Everything you need in just one subscription!',
          'Free SSL + Free CDN + Free Business Emails.': 'Free SSL + Free CDN + Free Business Emails.',
          'Limited Time Offer: Free Web Hosting + Free Top-Level Domain +': 'Limited Time Offer: Free Web Hosting + Free Top-Level Domain +',
          'with Veximus.': 'with Veximus.',
          Portfolio: 'Portfolio',
          Showcase: 'Showcase',
          Website: 'Website'
        },
        'zh-MY': {
          'Develop your': '无论您想开发',
          'E-commerce': '电子商务',
          Blog: '博客',
          'Everything you need in just one subscription!': '仅仅需要一项订阅，即可满足您所有的需求！',
          'Free SSL + Free CDN + Free Business Emails.': '免费SSL + 免费CDN + 免费商务电子邮箱。',
          'Limited Time Offer: Free Web Hosting + Free Top-Level Domain +': '限时优惠：免费网络托管 + 免费顶级域名 +',
          'with Veximus.': '微星都能满足您。',
          Portfolio: '作品集',
          Showcase: '展示集',
          Website: '网站'
        },
        'ms-MY': {
          'Develop your': 'Binakan',
          'E-commerce': 'E-dagang',
          Blog: 'Blog',
          'Everything you need in just one subscription!': 'Semua yang anda perlukan hanya dalam satu pelan langganan!',
          'Free SSL + Free CDN + Free Business Emails.': 'SSL Percuma + CDN Percuma + E-mel perniagaan percuma.',
          'Limited Time Offer: Free Web Hosting + Free Top-Level Domain +': 'Tawaran Masa Terhad: Hosting Web Percuma + Domain Peringkat Atasan Percuma +',
          'with Veximus.': 'Dengan Veximus.',
          Portfolio: 'Portfolio',
          Showcase: 'Pameran',
          Website: 'Website'
        }
      }
    });

    return { t };
  }
};
</script>
